import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Minus, Plus, QrCode, RotateCcw, X } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import LotScanner from '@/components/haccp/LotScanner';
import ConfirmActionDialog from '@/components/haccp/ConfirmActionDialog';
import QuantityKeypad from '@/components/haccp/QuantityKeypad';
import { convertUnitQuantity, formatUnitShort, limitUnitPrecision, normalizeUnitValue } from '@/lib/units';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const statusOf = (row) => row?.status || 'pending';
const dateLabel = (value) => (value ? new Date(value).toLocaleDateString('it-IT') : '—');
const tidyQty = (value) => Math.round((Number(value) + Number.EPSILON) * 10000) / 10000;
const INTEGER_UNITS = new Set(['pz', 'conf', 'vaschetta', 'busta', 'cartone', 'g', 'mg', 'ml', 'cl']);
const quantityStep = (unit) => (INTEGER_UNITS.has(normalizeUnitValue(unit)) ? 1 : 0.01);
const quantityDecimals = (unit) => (normalizeUnitValue(unit) === 'pz' ? 1 : 2);
const formatQuantity = (value, unit) => {
  if (value === '' || value === null || value === undefined) return '—';
  const number = Number(value);
  return Number.isFinite(number)
    ? new Intl.NumberFormat('it-IT', { useGrouping: false, maximumFractionDigits: quantityDecimals(unit) }).format(number)
    : '—';
};
const formatQuantityInput = (value, unit) => {
  if (value === '' || value === null || value === undefined) return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return value ?? '';
  const fixed = number.toFixed(quantityDecimals(unit));
  return fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
};
const roundQuantity = (value, unit) => {
  const factor = 10 ** quantityDecimals(unit);
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
};

function LotCombobox({
  open,
  onOpenChange,
  options,
  selectedLot,
  status,
  ingredientName,
  onSelect,
  disabled
}) {
  const selectedLabel = selectedLot
    ? `${selectedLot.lot_number || 'Lotto'} · ${selectedLot.current_quantity} ${selectedLot.unit}`
    : '';

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-11 w-full min-w-0 justify-between px-3 text-left font-normal"
        >
          <span className={`min-w-0 truncate ${selectedLot ? 'text-foreground' : 'text-muted-foreground'}`}>
            {selectedLot
              ? status === 'substituted'
                ? `${selectedLabel} · sostituzione`
                : selectedLabel
              : options.length
                ? 'Cerca o seleziona lotto'
                : 'Nessun lotto disponibile'}
          </span>
          <span className="ml-2 shrink-0 text-xs text-muted-foreground">⌄</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,430px)] p-0 sm:w-[420px]">
        <Command>
          <CommandInput placeholder="Cerca numero lotto o prodotto..." />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>Nessun lotto trovato.</CommandEmpty>
            {options.map((lot, lotIndex) => (
              <CommandItem
                key={lot.lot_id}
                value={`${lot.lot_number || ''} ${lot.commercial_name || ingredientName(lot.ingredient_id)} ${lot.current_quantity || ''} ${lot.unit || ''} ${dateLabel(lot.expiry_date)}`}
                onSelect={() => onSelect(lot.lot_id)}
                className="items-start py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    {lotIndex === 0 && (
                      <span className="shrink-0 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-700">
                        FEFO
                      </span>
                    )}
                    <span className="truncate font-semibold">{lot.lot_number || 'Lotto'}</span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {lot.commercial_name || ingredientName(lot.ingredient_id)} · {lot.current_quantity} {lot.unit} · scad. {dateLabel(lot.expiry_date)}
                  </p>
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function BatchIngredientWizard({ rows, onRowsChange, lots, ingredients, productionDate }) {
  const [scannerIndex, setScannerIndex] = useState(null);
  const [openLotIndex, setOpenLotIndex] = useState(null);
  const [pendingSkip, setPendingSkip] = useState(null);
  const [pendingSubstitution, setPendingSubstitution] = useState(null);
  const [extraLots, setExtraLots] = useState([]);
  const [numericEditor, setNumericEditor] = useState(null);
  const touchNumericInput = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 1023px), (pointer: coarse)').matches;

  const ingredientMap = useMemo(
    () => new Map(ingredients.map((item) => [item.ingredient_id, item])),
    [ingredients]
  );

  const allLots = useMemo(() => {
    const byId = new Map();
    [...lots, ...extraLots].forEach((lot) => byId.set(lot.lot_id, lot));
    return [...byId.values()];
  }, [lots, extraLots]);

  const lotMap = useMemo(() => new Map(allLots.map((lot) => [lot.lot_id, lot])), [allLots]);
  const ingredientName = (id) => ingredientMap.get(id)?.name || 'Ingrediente';
  const expired = (lot) =>
    Boolean(lot?.expiry_date && productionDate && String(lot.expiry_date).slice(0, 10) < productionDate);

  const updateRow = (index, patch) =>
    onRowsChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));

  const eligibleLots = (row) =>
    allLots
      .filter(
        (lot) =>
          lot.ingredient_id === row.ingredient_id &&
          lot.status === 'active' &&
          !expired(lot) &&
          Number(lot.current_quantity) > 0 &&
          ingredientMap.get(lot.ingredient_id)?.type !== 'final_product' &&
          convertUnitQuantity(1, lot.unit, row.unit) !== null
      )
      .sort(
        (a, b) =>
          new Date(a.expiry_date || '2999-12-31') - new Date(b.expiry_date || '2999-12-31')
      );

  const availableQuantity = (row) =>
    tidyQty(
      eligibleLots(row).reduce((sum, lot) => {
        const converted = convertUnitQuantity(Number(lot.current_quantity || 0), lot.unit, row.unit);
        return converted === null ? sum : sum + converted;
      }, 0)
    );

  const lotError = (lot, row) => {
    if (!lot) return 'Lotto non disponibile';
    if (lot.status !== 'active') return 'Il lotto non è attivo';
    if (ingredientMap.get(lot.ingredient_id)?.type === 'final_product') {
      return 'Un prodotto finito non può essere usato come ingrediente';
    }
    if (expired(lot)) return `Il lotto ${lot.lot_number || ''} è scaduto per la data di produzione`;
    const converted = convertUnitQuantity(Number(row?.quantity_used), row?.unit, lot.unit);
    if (converted === null) return `Unità ${row?.unit || ''} non compatibile con il lotto (${lot.unit || ''})`;
    if (!(converted > 0)) return 'Inserisci una quantità maggiore di zero';
    if (converted > Number(lot.current_quantity || 0)) {
      return `Quantità insufficiente: disponibili ${lot.current_quantity} ${lot.unit}`;
    }
    return '';
  };

  useEffect(() => {
    let changed = false;
    const next = rows.map((row) => {
      const status = statusOf(row);
      if (status === 'pending' || status === 'skipped' || !row.lot_id) return row;
      const lot = lotMap.get(row.lot_id);
      if (!lot || lotError(lot, row)) {
        changed = true;
        return { ...row, lot_id: '', used_ingredient_id: null, status: 'pending' };
      }
      return row;
    });
    if (changed) onRowsChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionDate, allLots, rows]);

  const fetchLot = async (lotId) => {
    const local = lotMap.get(lotId);
    if (local) return local;
    try {
      const response = await fetch(`${BACKEND_URL}/api/lots/${lotId}`, { credentials: 'include' });
      if (!response.ok) return null;
      const remote = await response.json();
      setExtraLots((current) =>
        current.some((lot) => lot.lot_id === remote.lot_id) ? current : [...current, remote]
      );
      return remote;
    } catch {
      return null;
    }
  };

  const acceptLot = (index, lot, status) => {
    const row = rows[index];
    updateRow(index, { lot_id: lot.lot_id, used_ingredient_id: lot.ingredient_id, status });
    toast.success(
      status === 'substituted'
        ? `${ingredientName(row.ingredient_id)} sostituito con ${ingredientName(lot.ingredient_id)}`
        : `${ingredientName(row.ingredient_id)} · lotto ${lot.lot_number} verificato`
    );
  };

  const considerLot = async (lotId, explicitIndex = scannerIndex) => {
    const index = explicitIndex;
    const row = rows[index];
    if (!row) return false;
    const lot = await fetchLot(lotId);
    if (!lot) {
      toast.error('Il lotto non è disponibile per questa produzione');
      return false;
    }
    const error = lotError(lot, row);
    if (error) {
      toast.error(error);
      return false;
    }
    if (lot.ingredient_id === row.ingredient_id) {
      acceptLot(index, lot, 'completed');
      setScannerIndex(null);
      return true;
    }
    setScannerIndex(null);
    setPendingSubstitution({ lot, index, requestedId: row.ingredient_id });
    return false;
  };

  const confirmSubstitution = () => {
    const pending = pendingSubstitution;
    if (!pending) return;
    const row = rows[pending.index];
    const error = lotError(pending.lot, row);
    if (error) {
      setPendingSubstitution(null);
      toast.error(error);
      return;
    }
    acceptLot(pending.index, pending.lot, 'substituted');
    setPendingSubstitution(null);
  };

  const confirmSkip = () => {
    const index = pendingSkip;
    if (index === null) return;
    updateRow(index, { lot_id: '', used_ingredient_id: null, status: 'skipped' });
    setPendingSkip(null);
    toast.info(`${ingredientName(rows[index]?.ingredient_id)} non utilizzato`);
  };

  const changeQuantity = (index, value) => {
    const row = rows[index];
    const normalizedValue = limitUnitPrecision(value, row?.unit);
    const patch = {
      quantity_used: normalizedValue,
      quantity_overridden: Number(normalizedValue) !== Number(row?.recipe_quantity)
    };
    if (row?.lot_id) {
      const lot = lotMap.get(row.lot_id);
      if (lot && lotError(lot, { ...row, quantity_used: normalizedValue })) {
        Object.assign(patch, { lot_id: '', used_ingredient_id: null, status: 'pending' });
      }
    }
    updateRow(index, patch);
  };

  const adjustQuantity = (index, direction) => {
    const row = rows[index];
    const step = quantityStep(row.unit);
    const current = Number(row.quantity_used || 0);
    const next = roundQuantity(Math.max(step, current + direction * step), row.unit);
    changeQuantity(index, String(next));
  };

  const canIncrease = (row) => {
    if (!row?.lot_id) return true;
    const lot = lotMap.get(row.lot_id);
    if (!lot) return false;
    const next = roundQuantity(Number(row.quantity_used || 0) + quantityStep(row.unit), row.unit);
    return !lotError(lot, { ...row, quantity_used: next });
  };

  const openQuantityKeypad = (index) => {
    const row = rows[index];
    if (!row) return;
    setNumericEditor({ index, label: `Quantità utilizzata · ${ingredientName(row.ingredient_id)}`, value: String(formatQuantityInput(row.quantity_used, row.unit)), maxDecimals: quantityDecimals(row.unit) });
  };
  const confirmQuantityKeypad = (value) => {
    if (numericEditor) changeQuantity(numericEditor.index, value);
    setNumericEditor(null);
  };

  if (!rows.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-dashed bg-white p-5 text-center text-sm text-[#667085]">
        La ricetta non contiene ingredienti.
      </div>
    );
  }

  const completedCount = rows.filter((item) => statusOf(item) !== 'pending').length;
  const scannerRow = scannerIndex === null ? null : rows[scannerIndex];

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)] gap-2 sm:gap-3">
      <div className="flex items-center justify-between gap-3 px-0.5 sm:px-1">
        <div className="min-w-0">
          <h3 className="font-heading text-xl font-semibold text-[#172033] sm:text-2xl">Ingredienti</h3>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
            Seleziona il lotto realmente utilizzato e verifica la quantità per ogni ingrediente.
          </p>
        </div>
        <div className="shrink-0 rounded-lg border bg-white px-2.5 py-1.5 text-xs font-semibold text-[#667085] sm:px-3 sm:text-sm">
          {completedCount}/{rows.length} gestiti
        </div>
      </div>

      <section className="min-h-0 overflow-y-auto rounded-2xl border bg-white">
        <div className="sticky top-0 z-10 hidden grid-cols-[minmax(180px,1fr)_88px_minmax(260px,1.45fr)_minmax(210px,220px)_44px] items-center gap-3 border-b bg-slate-50/95 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#667085] backdrop-blur lg:grid">
          <span>Ingrediente</span>
          <span>Richiesto</span>
          <span>Lotto</span>
          <span>Quantità utilizzata</span>
        </div>

        <div className="divide-y">
          {rows.map((row, index) => {
            const status = statusOf(row);
            const options = eligibleLots(row);
            const selectedLot = lotMap.get(row.lot_id);
            const changedQuantity = Number(row.recipe_quantity) !== Number(row.quantity_used);
            const selectedError = selectedLot ? lotError(selectedLot, row) : '';
            const rowTone =
              status === 'completed'
                ? 'bg-green-50/20'
                : status === 'substituted'
                  ? 'bg-amber-50/25'
                  : status === 'skipped'
                    ? 'bg-slate-50/80'
                    : 'bg-white';

            return (
              <div
                key={`${row.ingredient_id || 'ingredient'}-${index}`}
                className={`relative grid grid-cols-1 items-start gap-x-2 gap-y-2 p-2.5 sm:gap-3 sm:p-3 md:grid-cols-[minmax(150px,.8fr)_minmax(230px,1.2fr)_minmax(205px,1fr)_44px] md:py-2 lg:grid-cols-[minmax(180px,1fr)_88px_minmax(260px,1.45fr)_minmax(210px,220px)_44px] lg:px-3 ${rowTone}`}
              >
                <div className="col-span-1 min-w-0 pr-10 md:pr-0">
                  <div className="flex min-w-0 items-center gap-2">
                    {status === 'pending' && <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700" title="Da verificare" aria-label="Da verificare"><AlertTriangle className="h-4 w-4" aria-hidden="true" /></span>}
                    {status === 'completed' && <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700" title="Verificato" aria-label="Verificato"><CheckCircle2 className="h-4 w-4" aria-hidden="true" /></span>}
                    {status === 'substituted' && <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700" title="Ingrediente sostituito e confermato" aria-label="Ingrediente sostituito e confermato"><AlertTriangle className="h-4 w-4" aria-hidden="true" /></span>}
                    {status === 'skipped' && <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500" title="Ingrediente non utilizzato" aria-label="Ingrediente non utilizzato"><X className="h-4 w-4" aria-hidden="true" /></span>}
                    <p className="min-w-0 flex-1 truncate font-semibold text-[#172033]" title={ingredientName(row.ingredient_id)}>{ingredientName(row.ingredient_id)}</p>
                  </div>
                  {status === 'substituted' && selectedLot && <p className="mt-0.5 truncate pl-8 text-[11px] font-medium text-amber-800" title={`Usato ${ingredientName(selectedLot.ingredient_id)}`}>Usato {ingredientName(selectedLot.ingredient_id)}</p>}
                  <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground lg:hidden">
                    Richiesto: <span className="font-semibold text-foreground">{formatQuantity(row.recipe_quantity ?? row.quantity_used, row.unit)} {formatUnitShort(row.unit)}</span>
                    {' · '}Disp. <span className="font-semibold text-foreground">{formatQuantity(availableQuantity(row), row.unit)} {formatUnitShort(row.unit)}</span>
                  </p>
                </div>

                <div className="hidden lg:block">
                  <p className="font-semibold text-[#172033]">{formatQuantity(row.recipe_quantity ?? row.quantity_used, row.unit)} {row.unit}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Disp. {formatQuantity(availableQuantity(row), row.unit)} {row.unit}</p>
                </div>

                <div className="order-3 min-w-0 md:order-none md:col-span-1">
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground lg:hidden">Lotto *</p>
                  <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_44px] items-start gap-2">
                    <div className="min-w-0">
                      {status === 'skipped' ? (
                        <div className="flex h-11 items-center gap-2 rounded-md border bg-slate-50 px-3 text-sm font-medium text-slate-600"><X className="h-4 w-4" /> Non utilizzato</div>
                      ) : (
                        <LotCombobox open={openLotIndex === index} onOpenChange={(open) => setOpenLotIndex(open ? index : null)} options={options} selectedLot={selectedLot} status={status} ingredientName={ingredientName} onSelect={(lotId) => { setOpenLotIndex(null); considerLot(lotId, index); }} disabled={options.length === 0 && !selectedLot} />
                      )}
                      <p className="mt-1 min-h-4 text-[11px] leading-4 text-muted-foreground" title={selectedError || (selectedLot ? `Disponibili ${selectedLot.current_quantity} ${selectedLot.unit} · scad. ${dateLabel(selectedLot.expiry_date)}` : options.length === 0 && status !== 'skipped' ? 'Nessun lotto attivo e non scaduto disponibile.' : '')}>
                        {selectedError ? <span className="font-medium text-red-600">{selectedError}</span> : selectedLot && status !== 'skipped' ? <>Disponibili {formatQuantity(selectedLot.current_quantity, selectedLot.unit)} {selectedLot.unit} · scad. {dateLabel(selectedLot.expiry_date)}</> : !selectedLot && options.length === 0 && status !== 'skipped' ? <span className="font-medium text-red-600">Nessun lotto attivo disponibile.</span> : '\u00a0'}
                      </p>
                    </div>
                    <Button type="button" variant="outline" size="icon" className="h-11 w-11 border-blue-600 bg-blue-600 text-white hover:border-blue-700 hover:bg-blue-700" onClick={() => setScannerIndex(index)} disabled={status === 'skipped'} aria-label={`Scansiona lotto per ${ingredientName(row.ingredient_id)}`} title="Scansiona QR lotto"><QrCode className="h-5 w-5" /></Button>
                  </div>
                </div>

                <div className="order-2 min-w-0 md:order-none md:col-span-1">
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground lg:hidden">Usata *</p>
                  {status === 'skipped' ? (
                    <div className="flex h-11 items-center justify-center rounded-md border bg-slate-50 text-sm font-semibold text-slate-500">—</div>
                  ) : (
                    <div className="grid grid-cols-[40px_minmax(64px,1fr)_40px_40px] items-center overflow-hidden rounded-md border bg-white sm:grid-cols-[44px_minmax(80px,1fr)_44px_40px]">
                      <button
                        type="button"
                        className="flex h-11 items-center justify-center border-r text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-35"
                        onClick={() => adjustQuantity(index, -1)}
                        disabled={Number(row.quantity_used || 0) <= quantityStep(row.unit)}
                        aria-label={`Riduci quantità di ${ingredientName(row.ingredient_id)}`}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <Input
                        className="h-11 min-w-0 rounded-none border-0 px-1 text-center tabular-nums shadow-none focus-visible:ring-0"
                        type="number"
                        min="0"
                        step={quantityStep(row.unit)}
                        value={formatQuantityInput(row.quantity_used, row.unit)}
                        readOnly={touchNumericInput}
                        onClick={() => touchNumericInput && openQuantityKeypad(index)}
                        onChange={(event) => changeQuantity(index, event.target.value)}
                        aria-label={`Quantità utilizzata di ${ingredientName(row.ingredient_id)}`}
                      />
                      <button
                        type="button"
                        className="flex h-11 items-center justify-center border-l text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-35"
                        onClick={() => adjustQuantity(index, 1)}
                        disabled={!canIncrease(row)}
                        aria-label={`Aumenta quantità di ${ingredientName(row.ingredient_id)}`}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      <span className="flex h-11 min-w-0 items-center justify-center overflow-hidden border-l bg-slate-50 px-0.5 text-[10px] font-semibold text-slate-600 sm:px-1 sm:text-[11px]">
                        {formatUnitShort(row.unit)}
                      </span>
                    </div>
                  )}
                  {changedQuantity && status !== 'skipped' && (
                    <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-amber-700">
                      <AlertTriangle className="h-3.5 w-3.5" /> Modificata
                    </p>
                  )}
                </div>

                <div className="absolute right-1 top-1 md:static md:col-start-4 md:row-start-1 lg:col-start-5">
                  {status === 'skipped' ? (
                    <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 text-slate-600 hover:bg-slate-100" onClick={() => updateRow(index, { status: 'pending' })} aria-label={`Ripristina ${ingredientName(row.ingredient_id)}`} title="Ripristina ingrediente"><RotateCcw className="h-4 w-4" /></Button>
                  ) : (
                    <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setPendingSkip(index)} aria-label={`Non usare ${ingredientName(row.ingredient_id)}`} title="Non usare ingrediente"><X className="h-5 w-5" /></Button>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      </section>

      <LotScanner
        open={scannerIndex !== null}
        onOpenChange={(open) => !open && setScannerIndex(null)}
        onResolved={(lotId) => considerLot(lotId, scannerIndex)}
        title={scannerRow ? `Scansiona ${ingredientName(scannerRow.ingredient_id)}` : 'Scansiona lotto'}
        targetLabel={scannerRow ? ingredientName(scannerRow.ingredient_id) : ''}
      />

      <QuantityKeypad open={!!numericEditor} label={numericEditor?.label || 'Quantità utilizzata'} value={numericEditor?.value || ''} maxDecimals={numericEditor?.maxDecimals} onClose={() => setNumericEditor(null)} onConfirm={confirmQuantityKeypad} />

      <ConfirmActionDialog
        open={pendingSkip !== null}
        onOpenChange={(open) => !open && setPendingSkip(null)}
        title="Non utilizzare questo ingrediente?"
        description="Nessun lotto verrà scaricato. La scelta resterà registrata nella produzione."
        confirmLabel="Conferma"
        destructive={false}
        onConfirm={confirmSkip}
      />

      <ConfirmActionDialog
        open={Boolean(pendingSubstitution)}
        onOpenChange={(open) => !open && setPendingSubstitution(null)}
        title="Confermare la sostituzione?"
        description={
          pendingSubstitution
            ? `Ingrediente richiesto: ${ingredientName(
                pendingSubstitution.requestedId
              )}. Ingrediente scansionato: ${ingredientName(
                pendingSubstitution.lot.ingredient_id
              )}. La sostituzione resterà registrata.`
            : ''
        }
        confirmLabel="Conferma sostituzione"
        destructive={false}
        onConfirm={confirmSubstitution}
      />
    </div>
  );
}
